import React, { useState } from 'react';
import { AlertTriangle, Copy, Check, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { LoadingSpinner } from '../ui/loading-states';
import { useGenerateQRSecret } from '../../hooks/settings/useSettings';

interface GenerateQRSecretDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GenerateQRSecretDialog: React.FC<GenerateQRSecretDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { mutate: generateSecret, isPending, isSuccess, data, error } = useGenerateQRSecret();
  const [copied, setCopied] = useState(false);

  const handleGenerate = () => {
    generateSecret();
  };

  const handleCopy = async () => {
    if (data?.qr_secret) {
      await navigator.clipboard.writeText(data.qr_secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset state after closing animation
    setTimeout(() => {
      setCopied(false);
    }, 300);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Генерация нового QR секрета</DialogTitle>
          <DialogDescription>
            Создать новый криптографически стойкий ключ для подписи QR-кодов
          </DialogDescription>
        </DialogHeader>

        {!isSuccess ? (
          <>
            {/* Warning message */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-yellow-900">
                  Внимание! Важная информация
                </p>
                <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                  <li>Все ранее сгенерированные QR-коды станут недействительными</li>
                  <li>Потребуется заново распечатать QR-коды для всех локаций</li>
                  <li>Сотрудники не смогут использовать старые QR-коды</li>
                  <li>Это действие нельзя отменить</li>
                </ul>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  Ошибка: {error instanceof Error ? error.message : 'Не удалось сгенерировать секрет'}
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isPending}>
                Отмена
              </Button>
              <Button onClick={handleGenerate} disabled={isPending}>
                {isPending ? (
                  <>
                    <LoadingSpinner className="mr-2 h-4 w-4" />
                    Генерация...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Сгенерировать новый секрет
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            {/* Success state - show new secret */}
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm font-medium text-green-900">
                  ✓ Новый QR секрет успешно создан
                </p>
                <p className="text-xs text-green-700 mt-1">
                  {data?.message}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Новый секретный ключ:
                </label>
                <div className="bg-gray-100 rounded-md p-3 font-mono text-xs break-all">
                  {data?.qr_secret}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="w-full"
                >
                  {copied ? (
                    <>
                      <Check className="mr-2 h-4 w-4 text-green-600" />
                      Скопировано
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Копировать ключ
                    </>
                  )}
                </Button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  Следующие шаги:
                </p>
                <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Перейдите в раздел "Локации"</li>
                  <li>Заново сгенерируйте QR-коды для всех локаций</li>
                  <li>Распечатайте и разместите новые QR-коды</li>
                  <li>Удалите старые QR-коды</li>
                </ol>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose} className="w-full">
                Закрыть
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
